import { createContext, useContext } from 'react';

const translations = {
  en: {
    // Nav tabs
    'nav.overview': 'My Overview',
    'nav.close': 'Close & Review',
    'nav.fpa': 'FP&A',
    'nav.investor': 'Investor',
    'nav.yearend': 'Year-End',

    // Login
    'login.title': 'Sign in to continue',
    'login.email': 'Email',
    'login.password': 'Password',
    'login.submit': 'Sign in',
    'login.confidential': 'Demo Medical Devices Inc. -- Confidential',
    'login.mfa.title': 'Two-Factor Authentication',
    'login.mfa.code': 'Enter 6-digit code',
    'login.mfa.verify': 'Verify',

    // Header
    'header.enableMfa': 'Enable MFA',
    'header.signOut': 'Sign out',

    // KPI labels (Investor)
    'kpi.totalLiquidity': 'Total Liquidity',
    'kpi.runway': 'Runway',
    'kpi.avgBurn': 'Avg Monthly Burn',
    'kpi.capitalRaised': 'Capital Raised',
    'kpi.headcount': 'Headcount',
    'kpi.runwaySub': 'Based on 6-mo avg burn',
    'kpi.burnSub': '6-month trailing average',

    // Investor sections
    'inv.capitalStructure': 'Capital Structure',
    'inv.capitalStructureSub': 'Click a series to see deployment details',
    'inv.cashDeployment': 'Cash Deployment',
    'inv.burnRate': 'Burn Rate',
    'inv.deployed': 'deployed',
    'inv.remaining': 'Remaining',
    'inv.last12mo': 'Last 12mo',
    'inv.ytd': 'YTD',
    'inv.all': 'All',
    'inv.actual': 'Actual',
    'inv.projected': 'Projected',
    'inv.cashBalance': 'Cash balance (EOM)',
    'inv.avgLabel': '6-mo avg',
    'inv.monthlyBurn': 'Monthly Burn',

    // Roadmap
    'roadmap.title': '2026 EFS Roadmap',
    'roadmap.subtitle': 'Up to 1st patient in USA/CAN',
    'roadmap.today': 'Today',
    'roadmap.operations': 'OPERATIONS',
    'roadmap.externalVV': 'EXTERNAL V&V',
    'roadmap.dvvInHouse': 'DVV IN-HOUSE',
    'roadmap.clinicalReg': 'CLINICAL & REG.',

    // IP
    'ip.title': 'Intellectual Property',
    'ip.filed': 'Filed',
    'ip.granted': 'Granted',
    'ip.pending': 'Pending',
    'ip.countries': 'Countries',
    'ip.coverage': 'Coverage',
    'ip.strategy': '2026 Strategy',
    'ip.fto': 'FTO analysis planned after design freeze, before next capital raise',

    // Headcount
    'hc.title': 'Headcount Over Time',
    'hc.allCompany': 'All Company',
    'hc.employees': 'employees',
    'hc.departures': 'departures',
    'hc.vs12mo': 'vs 12mo ago',
    'hc.arrivals': 'Arrivals',
    'hc.departuresLabel': 'Departures',

    // Investor tabs
    'inv.tab.dashboard': 'Dashboard',
    'inv.tab.reporting': 'Reporting',
    'inv.reporting.coming': 'Investor reporting module is under development.',

    // Footer
    'footer.confidential': 'Demo Medical Devices Inc. -- Confidential -- Data as of February 28, 2026',

    // Dashboard (Controller)
    'dash.burnRate': 'Burn Rate Trend',
    'dash.budgetUtil': 'Budget Utilization',
    'dash.spendByDept': 'Spend by Department',
    'dash.closeStatus': 'Month-End Close',
    'dash.onTime': 'On time',
    'dash.dueSoon': 'Due soon',
    'dash.overdue': 'Overdue',
    'dash.done': 'Done',
    'dash.blocked': 'Blocked',
    'dash.notStarted': 'Not started',
    'dash.noTasksCompleted': 'No tasks completed',

    // Sidebar legend
    'sidebar.statusLegend': 'Status legend',
    'sidebar.status.notStarted': 'Not started',
    'sidebar.status.inProgress': 'In progress',
    'sidebar.status.done': 'Done — pending review',
    'sidebar.status.reviewed': 'Reviewed',
    'sidebar.status.blocked': 'Blocked',
    'dash.customize': 'Customize Dashboard',
    'dash.download': 'Download',
    'dash.cashPosition': 'Cash Position',
    'dash.gics': 'GICs & Savings',
    'dash.fxRate': 'FX Rate',
    'dash.overall': 'Overall',
    'dash.budget': 'Budget',
    'dash.actualLabel': 'Actual',
    'dash.remaining': 'remaining',
    'dash.mtd': 'MTD',
    'dash.ytd': 'YTD',
    'dash.custom': 'Custom',

    // Close Checklist
    'close.tasks': 'tasks',
    'close.completed': 'completed',
    'close.review': 'Review',
    'close.blocked': 'Blocked',

    // Company Setup
    'setup.companyName': 'Company Name',
    'setup.erp': 'ERP / Accounting System',
    'setup.industry': 'Industry',
    'setup.fyeEnd': 'Fiscal Year End',
    'setup.allSet': "You're all set!",

    // Forecast
    'forecast.title': 'Financial Forecast',
    'forecast.scenario': 'Scenario',
  },
  fr: {
    // Nav tabs
    'nav.overview': 'Mon Apercu',
    'nav.close': 'Cloture & Revision',
    'nav.fpa': 'FP&A',
    'nav.investor': 'Investisseur',
    'nav.yearend': 'Fin d\'annee',

    // Login
    'login.title': 'Connectez-vous pour continuer',
    'login.email': 'Courriel',
    'login.password': 'Mot de passe',
    'login.submit': 'Se connecter',
    'login.confidential': 'Demo Medical Devices Inc. -- Confidentiel',
    'login.mfa.title': 'Authentification a deux facteurs',
    'login.mfa.code': 'Entrez le code a 6 chiffres',
    'login.mfa.verify': 'Verifier',

    // Header
    'header.enableMfa': 'Activer MFA',
    'header.signOut': 'Deconnexion',

    // KPI labels (Investor)
    'kpi.totalLiquidity': 'Liquidite totale',
    'kpi.runway': 'Autonomie',
    'kpi.avgBurn': 'Depenses moy. mensuelles',
    'kpi.capitalRaised': 'Capital leve',
    'kpi.headcount': 'Effectif',
    'kpi.runwaySub': 'Base sur la moy. 6 mois',
    'kpi.burnSub': 'Moyenne mobile 6 mois',

    // Investor sections
    'inv.capitalStructure': 'Structure du capital',
    'inv.capitalStructureSub': 'Cliquez sur une serie pour voir les details',
    'inv.cashDeployment': 'Deploiement de tresorerie',
    'inv.burnRate': 'Taux de depenses',
    'inv.deployed': 'deploye',
    'inv.remaining': 'Restant',
    'inv.last12mo': '12 derniers mois',
    'inv.ytd': 'Cumul annuel',
    'inv.all': 'Tout',
    'inv.actual': 'Reel',
    'inv.projected': 'Projete',
    'inv.cashBalance': 'Solde de tresorerie (fin de mois)',
    'inv.avgLabel': 'Moy. 6 mois',
    'inv.monthlyBurn': 'Depenses mensuelles',

    // Roadmap
    'roadmap.title': 'Feuille de route EFS 2026',
    'roadmap.subtitle': 'Jusqu\'au 1er patient aux USA/CAN',
    'roadmap.today': 'Aujourd\'hui',
    'roadmap.operations': 'OPERATIONS',
    'roadmap.externalVV': 'V&V EXTERNE',
    'roadmap.dvvInHouse': 'DVV INTERNE',
    'roadmap.clinicalReg': 'CLINIQUE & REG.',

    // IP
    'ip.title': 'Propriete intellectuelle',
    'ip.filed': 'Deposees',
    'ip.granted': 'Accordees',
    'ip.pending': 'En attente',
    'ip.countries': 'Pays',
    'ip.coverage': 'Couverture',
    'ip.strategy': 'Strategie 2026',
    'ip.fto': 'Analyse FTO prevue apres le gel de conception, avant la prochaine levee de fonds',

    // Headcount
    'hc.title': 'Evolution des effectifs',
    'hc.allCompany': 'Toute l\'entreprise',
    'hc.employees': 'employes',
    'hc.departures': 'departs',
    'hc.vs12mo': 'vs il y a 12 mois',
    'hc.arrivals': 'Arrivees',
    'hc.departuresLabel': 'Departs',

    // Investor tabs
    'inv.tab.dashboard': 'Tableau de bord',
    'inv.tab.reporting': 'Rapports',
    'inv.reporting.coming': 'Le module de rapports investisseurs est en cours de developpement.',

    // Footer
    'footer.confidential': 'Demo Medical Devices Inc. -- Confidentiel -- Donnees au 28 fevrier 2026',

    // Dashboard (Controller)
    'dash.burnRate': 'Tendance des depenses',
    'dash.budgetUtil': 'Utilisation du budget',
    'dash.spendByDept': 'Depenses par departement',
    'dash.closeStatus': 'Cloture de fin de mois',
    'dash.onTime': 'A temps',
    'dash.dueSoon': 'Bientot du',
    'dash.overdue': 'En retard',
    'dash.done': 'Termine',
    'dash.blocked': 'Bloque',
    'dash.notStarted': 'Non commence',
    'dash.noTasksCompleted': 'Aucune tache completee',

    // Sidebar legend
    'sidebar.statusLegend': 'Legende des statuts',
    'sidebar.status.notStarted': 'Non commence',
    'sidebar.status.inProgress': 'En cours',
    'sidebar.status.done': 'Termine — revision en attente',
    'sidebar.status.reviewed': 'Revise',
    'sidebar.status.blocked': 'Bloque',
    'dash.customize': 'Personnaliser le tableau',
    'dash.download': 'Telecharger',
    'dash.cashPosition': 'Position de tresorerie',
    'dash.gics': 'CPG & Epargne',
    'dash.fxRate': 'Taux de change',
    'dash.overall': 'Global',
    'dash.budget': 'Budget',
    'dash.actualLabel': 'Reel',
    'dash.remaining': 'restant',
    'dash.mtd': 'Mois',
    'dash.ytd': 'Cumul',
    'dash.custom': 'Personnalise',

    // Close Checklist
    'close.tasks': 'taches',
    'close.completed': 'completees',
    'close.review': 'Revision',
    'close.blocked': 'Bloque',

    // Company Setup
    'setup.companyName': 'Nom de l\'entreprise',
    'setup.erp': 'ERP / Systeme comptable',
    'setup.industry': 'Industrie',
    'setup.fyeEnd': 'Fin d\'annee fiscale',
    'setup.allSet': 'Tout est pret!',

    // Forecast
    'forecast.title': 'Previsions financieres',
    'forecast.scenario': 'Scenario',
  },
};

export const LanguageContext = createContext('en');
export const useLanguage = () => useContext(LanguageContext);

export function useTranslation() {
  const lang = useContext(LanguageContext);
  return (key, fallback) => translations[lang]?.[key] || translations.en?.[key] || fallback || key;
}
